import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import {
  Button,
  FlashMessage,
  Heading,
  IconSmile,
  InputTiles,
  SheetLayoutContent,
  SheetLayoutContentWrapper,
  SheetLayoutHeader,
} from '../../../brenger-shared-ui';
import { useTypedSelector } from '../../../hooks';
import { useDateOptions } from '../../../hooks/useDateOptions';
import { RootState, StopType } from '../../../types';
import FooterLayout from '../containers/FooterLayout';
import { actions, getPickupSituation, getTbAuctionPickupDateAsString } from '../ducks';
import { proceed } from '../proceed';
import { InputCheckbox } from '@brenger/react';

export enum DateStepParam {
  DONT_RESET = 'do_not_reset_date=true',
}

export const SubStepDate: React.FC<RouteComponentProps> = ({ location }) => {
  const dispatch = useDispatch();
  const dates = useDateOptions();
  const chosen = useTypedSelector(state => state.generalTransport.choices?.[`${dates.meta.stopType}_date`]);
  const differentDeliveryDate = useTypedSelector(state => state.generalTransport.choices?.different_delivery_date);
  const auctionPickupDate = useTypedSelector(getTbAuctionPickupDateAsString);
  const error = useTypedSelector((state: RootState) => state.generalTransport.error);
  const isAuction = useTypedSelector(getPickupSituation) === 'auction';

  React.useEffect(() => {
    dispatch(actions.setProgress(4));
    dispatch(actions.addLastVisitTime());
  }, [location.pathname]);

  React.useEffect(() => {
    if (!window.location.search.includes(DateStepParam.DONT_RESET)) {
      dispatch(actions.resetChoice([`${dates.meta.stopType}_date`]));
      dispatch(actions.getPrice());
    }
    dates.getInitialOptions();
  }, [dates.meta.stopType]);

  return (
    <SheetLayoutContentWrapper>
      {dates.meta.hasSingleCollectionDay && dates.meta.stopType === StopType.DELIVERY && (
        <SheetLayoutHeader>
          <Heading size={2}>
            <Translate id={'request_flow.BVA.integration.pickup_date_auction'} />
          </Heading>
          <p>{auctionPickupDate}</p>
        </SheetLayoutHeader>
      )}
      <SheetLayoutHeader>
        <Heading size={2}>
          <Translate id={dates.meta.title} />
        </Heading>
        {dates.meta.aboveMaxDistance && (
          <p>
            <Translate id={'request_flow.date.sub_title_long_distance' + (isAuction ? '_auction' : '')} />
          </p>
        )}
      </SheetLayoutHeader>
      <SheetLayoutContent>
        <div className={'question-wrapper'}>
          <div>
            {dates.loading && !dates.options.length && <IconSmile spinning={true} />}
            {dates.options.length !== 0 && (
              <>
                <InputTiles
                  value={chosen}
                  id={`${dates.meta.stopType}.date`}
                  name={`${dates.meta.stopType}.date`}
                  options={dates.options}
                  compact={true}
                  onChange={(newDate: string) => {
                    dispatch(actions.setChoice({ [`${dates.meta.stopType}_date`]: newDate }));
                  }}
                  onClick={() => proceed({ updatePriceFirst: true })}
                  maxDelayIndex={7}
                />
                {dates.getMore && (
                  <Button
                    dataQaId="show_more_dates"
                    fullWidth={true}
                    type="button"
                    buttonStyle="outline"
                    iconPos="left"
                    onClick={dates.getMore}
                  >
                    <span className="icon-wrapper">
                      <IconSmile smileDirection={'down'} spinning={dates.loading} />
                    </span>
                    <Translate id={'request_flow.date.show_more_dates'} />
                  </Button>
                )}
                {dates.showDifferentDeliveryDate && (
                  <div className="flex gap-2 items-center">
                    <InputCheckbox
                      id={'different_delivery_date'}
                      type="checkbox"
                      checked={!!differentDeliveryDate}
                      onChange={() => dispatch(actions.setChoice({ different_delivery_date: !differentDeliveryDate }))}
                    />
                    <label htmlFor={'different_delivery_date'}>
                      <Translate id={'request_flow.date.select_different_delivery_date'} />
                    </label>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {error && <FlashMessage type={'error'} message={error} />}
      </SheetLayoutContent>
      <FooterLayout />
    </SheetLayoutContentWrapper>
  );
};
